import { useAtomValue } from 'jotai';
import { CircleAlert } from 'lucide-react';
import React from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { Form, Icon, useToaster, withConfirmation } from '@crossnokaye/ux-common';
import { Button } from '@crossnokaye/ux-common/ckds';

import { baseTimeZoneAtom } from '@/state/time.state';

const ButtonWithConfirmation = withConfirmation(Button);

type ShutdownDialogProps = {
  cancelText: string;
  children: React.ReactNode;
  confirmText: string;
  description: string;
  form: UseFormReturn<any>;
  id: string;
  onSubmit: (data: any) => void;
  title: string;
  triggerText: string;
};

export const ShutdownDialog: React.FC<ShutdownDialogProps> = ({
  cancelText,
  children,
  confirmText,
  description,
  form,
  id,
  onSubmit,
  title,
  triggerText,
}) => {
  const { addErrorToast } = useToaster();

  const timezone = useAtomValue(baseTimeZoneAtom);

  useEffect(() => {
    if (!timezone) {
      addErrorToast(
        'Timezone not found. We were unable to modify shutdowns. Please contact support.'
      );
    }
  }, [timezone, addErrorToast]);

  const canSave = useMemo(
    () =>
      form.formState.isDirty && !form.formState.isSubmitting && form.formState.isValid && timezone,
    [form.formState.isDirty, form.formState.isSubmitting, form.formState.isValid, timezone]
  );

  const handleDialogChange = useCallback(() => {
    form.reset();
  }, [form]);

  const handleSubmit = useCallback(async () => {
    await form.handleSubmit(onSubmit)();
    form.reset();
  }, [form, onSubmit]);

  const Title = (
    <div className={'flex items-center gap-4 text-foreground'}>
      <Icon
        data-testid={`${id}-shutdown-dialog-alert-icon`}
        icon={CircleAlert}
        backgroundColor={'bg-ckds-base-critical'}
        variant="rounded"
        size="md"
        color="text-foreground"
      />
      {title}
    </div>
  );

  const Description = (
    <div className={'flex flex-col gap-4'}>
      <span>{description}</span>
      <Form {...form}>{children}</Form>
    </div>
  );

  return (
    <ButtonWithConfirmation
      confirmationTitle={Title}
      confirmationDescription={Description}
      confirmLabel={confirmText}
      cancelLabel={cancelText}
      confirmVariant={'destructive'}
      disableConfirm={!canSave}
      onConfirm={handleSubmit}
      onCancel={handleDialogChange}
      variant={'inverse'}
    >
      {triggerText}
    </ButtonWithConfirmation>
  );
};
